<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from "vue";
import { toDataURL as qrToDataURL } from "qrcode";

import { apiClient, ApiError } from "@/api/client";

const props = defineProps<{
  sessionId: string;
  // The currently-attached image (if any). Only one photo is attached per
  // turn (PRD §6.7 hard cap), so this is a single string, not a list.
  attachmentId: string | null;
}>();

const emit = defineEmits<{
  // Fired when the phone has uploaded; parent stashes the id and
  // includes it in the next answer-submit turn.
  attached: [attachmentId: string];
  // Fired when the student removes the attached photo; parent clears its
  // stashed attachmentId.
  cleared: [];
}>();

type PanelState = "closed" | "creating" | "waiting" | "connected" | "error";

const state = ref<PanelState>("closed");
const errorMsg = ref<string | null>(null);
const token = ref<string | null>(null);
const shortCode = ref<string | null>(null);
const captureUrl = ref<string | null>(null);
const qrDataUrl = ref<string | null>(null);
const consecutive5xx = ref(0);
let pollHandle: ReturnType<typeof setInterval> | null = null;

const POLL_INTERVAL_MS = 1500;
// Number of consecutive 5xx polls we tolerate before showing an
// error. One transient burp shouldn't kill a pairing in progress,
// but a sustained outage shouldn't spin silently either.
const MAX_CONSECUTIVE_5XX = 3;

// The connected pill is a separate visual state from the panel; once
// the parent has an attachmentId, we collapse the panel and show the
// pill regardless of internal state.
const showConnectedPill = computed(() => props.attachmentId !== null);

watch(
  () => props.attachmentId,
  (next) => {
    if (next === null && state.value === "connected") {
      // Parent cleared the attachment (e.g. after submit) — reset.
      state.value = "closed";
    }
  },
);

async function openPanel(): Promise<void> {
  if (state.value === "creating" || state.value === "waiting") return;
  state.value = "creating";
  errorMsg.value = null;
  try {
    const res = await apiClient.createPairingToken(props.sessionId);
    token.value = res.token;
    shortCode.value = res.short_code;
    captureUrl.value = res.capture_url;
    qrDataUrl.value = await qrToDataURL(res.capture_url, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 192,
    });
    state.value = "waiting";
    startPolling();
  } catch (e) {
    errorMsg.value =
      e instanceof ApiError
        ? (e.code ?? e.message)
        : "Could not start pairing.";
    state.value = "error";
  }
}

function closePanel(): void {
  stopPolling();
  state.value = "closed";
  token.value = null;
  shortCode.value = null;
  captureUrl.value = null;
  qrDataUrl.value = null;
}

function startPolling(): void {
  stopPolling();
  pollHandle = setInterval(pollOnce, POLL_INTERVAL_MS);
}

function stopPolling(): void {
  if (pollHandle !== null) {
    clearInterval(pollHandle);
    pollHandle = null;
  }
}

async function pollOnce(): Promise<void> {
  if (token.value === null) return;
  try {
    const res = await apiClient.getPairingTokenStatus(token.value);
    consecutive5xx.value = 0;
    if (res.attachment_id) {
      stopPolling();
      state.value = "connected";
      emit("attached", res.attachment_id);
    } else if (res.expired) {
      stopPolling();
      state.value = "error";
      errorMsg.value = "Pairing expired. Try again.";
    }
  } catch (e) {
    // Failure modes the user needs to know about. Swallowing a 401 / 500 /
    // CORS failure during pairing would look identical to a happy "still
    // waiting for the phone" — the spinner stays up forever and the actual
    // cause is invisible. Each branch below stops polling and shows a
    // status-appropriate message.
    if (e instanceof ApiError) {
      if (e.status === 404) {
        stopPolling();
        state.value = "error";
        errorMsg.value = "Pairing not found.";
      } else if (e.status === 401 || e.status === 403) {
        stopPolling();
        state.value = "error";
        errorMsg.value =
          "Your session expired. Refresh the page and try again.";
      } else if (e.status >= 500) {
        consecutive5xx.value += 1;
        if (consecutive5xx.value >= MAX_CONSECUTIVE_5XX) {
          stopPolling();
          state.value = "error";
          errorMsg.value = "Server error during pairing. Try again.";
        }
      }
      // Other 4xx (rare here) are transient — keep polling.
    }
    // Genuinely transient (network blip, no ApiError) — keep polling.
  }
}

function disconnect(): void {
  emit("cleared");
  closePanel();
}

onUnmounted(() => {
  stopPolling();
});
</script>

<template>
  <div class="pairing-panel">
    <!-- tutor-i9v — photo-attached confirmation. Shown whenever the
         parent has an attachment, regardless of internal pairing
         state. It describes the photo rather than the pairing, so
         students do not think they still have to take a picture; the
         checkmark + explicit "Remove photo" makes the attachment
         status unambiguous. -->
    <div
      v-if="showConnectedPill"
      class="photo-attached"
      role="status"
      data-testid="photo-attached-pill"
    >
      <span class="photo-attached-check" aria-hidden="true">✓</span>
      <span class="photo-attached-label">Photo attached</span>
      <button
        type="button"
        class="photo-attached-remove"
        aria-label="Remove the attached photo"
        data-testid="pairing-photo-remove"
        @click="disconnect"
      >
        Remove
      </button>
    </div>

    <!-- tutor-ila — labelled trigger so students notice and
         understand the affordance. An icon-only square underplays
         the feature; a 44pt-tall labelled button meets the mobile
         tap-target threshold and reads as a deliberate secondary CTA
         next to "Mark my answer". Copy is "Handwrite answer" because
         the action from the student's perspective is "write my answer
         by hand on paper" — the photo capture is the mechanism, not
         the goal. -->
    <button
      v-else-if="state === 'closed'"
      type="button"
      class="camera-trigger"
      aria-label="Handwrite your answer on paper and attach a photo"
      title="Handwrite your answer and attach a photo from your phone"
      data-testid="pairing-camera-trigger"
      @click="openPanel"
    >
      <span class="camera-label">Handwrite answer</span>
    </button>

    <!-- Inline panel: open / waiting / creating / error. -->
    <div
      v-else
      class="pairing-popover"
      role="dialog"
      aria-labelledby="pairing-popover-title"
      data-testid="pairing-popover"
    >
      <div class="popover-header">
        <h3 id="pairing-popover-title">Pair your phone</h3>
        <button
          type="button"
          class="popover-close"
          aria-label="Close pairing"
          @click="closePanel"
        >
          ×
        </button>
      </div>

      <div v-if="state === 'creating'" class="popover-body">
        <p class="muted">Generating QR code…</p>
      </div>

      <div v-else-if="state === 'waiting'" class="popover-body">
        <p class="muted">
          Scan the code with your phone, then take a photo of your working.
        </p>
        <div v-if="qrDataUrl" class="qr-wrap">
          <img
            :src="qrDataUrl"
            alt="QR code linking to the phone capture page"
            class="qr-image"
            data-testid="pairing-qr-image"
          />
        </div>
        <p class="fallback-code-label">Can't scan? Type this on your phone:</p>
        <p class="fallback-code" data-testid="pairing-short-code">
          {{ shortCode }}
        </p>
        <p class="muted small">
          Code expires in ~10&nbsp;minutes. Open
          <code>{{ captureUrl }}</code> on the phone if needed.
        </p>
      </div>

      <div v-else-if="state === 'error'" class="popover-body">
        <p class="error" role="alert">
          {{ errorMsg ?? "Something went wrong." }}
        </p>
        <button class="secondary" type="button" @click="openPanel">
          Try again
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.pairing-panel {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.camera-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  /* tutor-ila — 44pt minimum tap target for mobile. Width grows
     with content; height pinned at 44px so it lines up next to
     the primary "Mark my answer" button. */
  min-height: 44px;
  padding: 0 16px;
  border-radius: var(--radius, 8px);
  border: 1px solid var(--rl-border-subtle, #e5e5e5);
  background: var(--rl-bg, #fff);
  cursor: pointer;
  font: inherit;
  font-size: 14px;
  line-height: 1.2;
  color: var(--rl-fg-1, #2b2b2b);
}

.camera-trigger:hover {
  background: var(--rl-bg-subtle, #f5f5f5);
  border-color: var(--rl-fg-1, #2b2b2b);
}

.camera-trigger:focus-visible {
  outline: 2px solid var(--rl-fg-1, #2b2b2b);
  outline-offset: 2px;
}

.camera-label {
  font-weight: 500;
}

/* tutor-i9v — photo-attached confirmation: a green check + explicit
   Remove button, so the attached state is unambiguous. */
.photo-attached {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 6px 8px 6px 14px;
  border-radius: var(--radius, 8px);
  border: 1px solid #2da44e;
  background: rgba(45, 164, 78, 0.08);
  font-size: 14px;
  color: var(--rl-fg-1, #2b2b2b);
}

.photo-attached-check {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #2da44e;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
}

.photo-attached-label {
  font-weight: 500;
}

.photo-attached-remove {
  margin-left: 4px;
  padding: 4px 10px;
  border: none;
  background: transparent;
  color: var(--rl-fg-2, #555);
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  border-radius: var(--radius, 8px);
}

.photo-attached-remove:hover {
  background: rgba(0, 0, 0, 0.05);
  color: var(--rl-fg-1, #2b2b2b);
}

.pairing-popover {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  width: 280px;
  padding: 16px;
  background: var(--rl-bg, #fff);
  border: 1px solid var(--rl-border-subtle, #e5e5e5);
  border-radius: var(--radius-lg, 12px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
  z-index: 50;
}

.popover-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.popover-header h3 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
}

.popover-close {
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: transparent;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  color: var(--rl-text-muted, #6b7280);
}

.popover-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.qr-wrap {
  display: flex;
  justify-content: center;
  margin: 4px 0;
}

.qr-image {
  width: 192px;
  height: 192px;
  display: block;
}

.fallback-code-label {
  margin: 8px 0 0 0;
  font-size: 12px;
  color: var(--rl-text-muted, #6b7280);
}

.fallback-code {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 4px;
  font-family: ui-monospace, SFMono-Regular, monospace;
  text-align: center;
}

.muted {
  color: var(--rl-text-muted, #6b7280);
  font-size: 13px;
  margin: 0;
}

.muted.small {
  font-size: 11px;
  word-break: break-all;
}

.error {
  color: #b91c1c;
  font-size: 13px;
  margin: 0 0 8px 0;
}
</style>
